import { useState, useRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import PropTypes from "prop-types";

/**
 * Pull-tab style menu component that works with both tap and swipe gestures
 * Creates an intuitive mobile menu experience with visual pull-tab feedback
 */
const PullTabMenu = ({
  isOpen,
  onToggle,
  children,
  className = "",
  menuRef = null,
}) => {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const internalMenuRef = useRef(null);
  const buttonRef = menuRef || internalMenuRef;

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
    e.stopPropagation();
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;

    // Only allow pulling down when closed, or up when open
    if ((!isOpen && diff > 0) || (isOpen && diff < 0)) {
      setDragY(Math.min(Math.abs(diff), 100)); // Cap at 100px
    }

    e.stopPropagation();
  };

  const handleTouchEnd = (e) => {
    if (dragY > 30) {
      // Threshold for activation
      onToggle();
    }
    setDragY(0);
    setIsDragging(false);
    e.stopPropagation();
  };

  const handleClick = (e) => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <div className={`relative ${className}`}>
      {/* Pull-tab button */}
      <button
        ref={buttonRef}
        className="relative inline-flex flex-col items-center justify-center 
                   px-4 py-2 rounded-b-lg bg-white dark:bg-gray-800 
                   shadow-lg border border-t-0 border-gray-200 dark:border-gray-700
                   hover:bg-gray-50 dark:hover:bg-gray-700
                   transition-all duration-200 sm:hidden
                   focus:outline-none focus:ring-2 focus:ring-offset-2 
                   focus:ring-gray-500 dark:focus:ring-gray-300"
        style={{
          transform: `translateY(${dragY * 0.3}px)`, // Subtle pull effect
          transition: isDragging ? "none" : "transform 200ms",
        }}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        aria-expanded={isOpen}
        aria-label={isOpen ? "Close main menu" : "Open main menu"}
      >
        <span className="text-base font-medium text-gray-700 dark:text-gray-300">
          {isOpen ? "Close" : "Menu"}
        </span>
        <div className="mt-0.5">
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400 animate-pulse" />
          )}
        </div>

        {/* Visual drag feedback */}
        {isDragging && dragY > 0 && (
          <div
            className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 
                       w-8 h-1 bg-gray-300 dark:bg-gray-600 rounded-full
                       transition-opacity"
            style={{ opacity: Math.min(dragY / 30, 1) }}
          />
        )}
      </button>

      {/* Menu content container */}
      {children}
    </div>
  );
};

PullTabMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node, // Optional - can be rendered via portal
  className: PropTypes.string,
  menuRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

export default PullTabMenu;
